import template from 'url-template';

import {
    FETCH_FAULT_BY_PRIORITY,
    FETCH_FAULT_BY_RULE,
    FETCH_WORST_PERFORMING_EQUIPMENT,
    FETCH_SITE_SCORE_DATA
} from '../actions/actionTypes';

import _ from 'lodash';

import moment from 'moment';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';
import { ConcurrencyManager } from 'axios-concurrency';

import { setBobLoading } from './bobActions';
import { handleNetworkError } from './applicationMessageActions';

const DashboardDataUrl = template.parse('/companies/{companyId}/healthdashboards{?limit,page,searchFilter,sort,tabView,clientId,siteId,equipmentId,systemId,businessTypeId,buildingTypeId,equipmentTypeId,systemTypeId,priority,export,startDate,endDate,excludeFault}');
const FaultsUrl = template.parse('/companies/{companyId}/faults-by-priority{?clientId,siteId,startDate,endDate,equipmentTypeId,systemTypeId}');
const FaultByRuleUrl = template.parse('/equipment-details{?CompanyId,ClientId,SiteId,StartDate,EndDate,equipmentTypeId,systemTypeId}');
const SitesUrl = template.parse('/clients/{clientId}/sites{?limit,page,searchFilter,sort}');
const AttributeLookupUrl = template.parse('/companies/{companyId}/attributes/{attributeId}/lookups');

const MAX_CONCURRENCY_REQUESTS = 10;

export const fetchFaultByRule = (CompanyId, ClientId, SiteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        options.StartDate = moment().local().format();
        options.EndDate = moment().local().format();
    }

    dispatch(setBobLoading('FetchExecutiveSummaryFaultByRule'));

    try {
        const response = await k2aBaseApi.get(
            FaultByRuleUrl.expand({
                CompanyId,
                ClientId,
                SiteId,
                ...options
            }),
            header
        );

        let data = _.get(response, 'data', []);
        data = _.orderBy(data, ['totalCount'], ['desc']);

        const meta = { totalCount: data.length };

        dispatch({
            type:    FETCH_FAULT_BY_RULE,
            payload: {
                data,
                meta
            }
        });
        dispatch(setBobLoading('FetchExecutiveSummaryFaultByRule'));
    } catch (error) {
        dispatch(setBobLoading('FetchExecutiveSummaryFaultByRule'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchFaultByPriority = (companyId, clientId, siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        options.startDate = null;
        options.endDate = null;
    }

    dispatch(setBobLoading('FetchExecutiveSummaryFaults'));

    try {
        const response = await k2aBaseApi.get(
            FaultsUrl.expand({
                companyId,
                clientId,
                siteId,
                ...options
            }),
            header
        );

        let data = _.get(response, 'data.summary', []);
        const meta = _.get(response, 'data.summaryMeta', {});

        if (!data) data = [];

        dispatch({
            type:    FETCH_FAULT_BY_PRIORITY,
            payload: {
                data,
                meta
            }
        });
        dispatch(setBobLoading('FetchExecutiveSummaryFaults'));
    } catch (error) {
        dispatch(setBobLoading('FetchExecutiveSummaryFaults'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchWorstPerformingEquipment = (companyId, clientId, siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.tabView = options.isSystem ? 'CompanySystem' : 'CompanyEquipment';
    const isCurrentTimeStamp = getState().dashboard.dashboardIsCurrentTimeStamp;
    if (siteId) {
        options.tabView = options.isSystem ? 'SiteSystem' : 'SiteEquipment';
    } else if (clientId) {
        options.tabView = options.isSystem ? 'ClientSystem' : 'ClientEquipment';
    }

    if (isCurrentTimeStamp) {
        options.startDate = null;
        options.endDate = null;
        options.excludeFault = false;
    }
    else {
        options.excludeFault = true;
    }
    options.sort = options.isSystem ? 'systemScore^asc' : 'equipmentScore^asc';
    options.limit = 1000;
    options.page = 1;

    dispatch(setBobLoading('FetchEquipment'));
    const manager = ConcurrencyManager(k2aBaseApi, MAX_CONCURRENCY_REQUESTS);
    let topTen = [];
    try {
        const response = await k2aBaseApi.get(
            DashboardDataUrl.expand({ companyId, clientId, siteId, ...options }),
            header
        );

        const meta = _.get(response, 'data.summaryMeta', {});
        let data = _.get(response, 'data.healthcheckSummaries', []);
        let allData = [];
        const total = meta.totalRecordCount;
        let totalPages = total / options.limit;
        let count;
        
        if (Number.isInteger(totalPages)) {
            count = totalPages;
        }
        else {
            count = totalPages + 1;
        }

        if (!data) data = [];

        let promises = [];
        for (let i = 2; i <= count; i++) {
            options.page = i;
            promises.push(
                k2aBaseApi.get(
                    DashboardDataUrl.expand({ 
                        companyId, 
                        clientId, 
                        siteId, 
                        ...options 
                    }),
                    header
                )
            );
        }

        let results = [];
        await Promise.all(promises.map(p => p))
            .then((result) => {
                result.forEach(r => {
                    results.push(r.data.healthcheckSummaries);
                });
            });

        allData = data.concat(...results);

        allData.forEach(entity => {
            const keys = Object.keys(entity).filter(item => item.toLowerCase().includes('score'));

            keys.forEach(key => {
                if (entity[key]) {
                    entity[key] = entity[key] * 100;
                }
            });

            entity.companyId = companyId;
        });

        topTen = (_.orderBy(allData, options.isSystem ? 'systemScore' : 'equipmentScore','asc')).slice(0, 10);

        dispatch({
            type:    FETCH_WORST_PERFORMING_EQUIPMENT,
            payload: {
                data: topTen,
                meta
            }
        });
        dispatch(setBobLoading('FetchEquipment'));
    } catch (error) {
        dispatch(setBobLoading('FetchEquipment'));
        dispatch(handleNetworkError(error));
    }
    finally {
        manager.detach();
    }
};

export const fetchSiteScoreData = (companyId, clientId, siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.tabView = 'CompanySite';
    const isCurrentTimeStamp = getState().dashboard.dashboardIsCurrentTimeStamp;

    if (clientId) options.tabView = 'ClientSite';

    if (isCurrentTimeStamp) {
        options.startDate = null;
        options.endDate = null;
        options.excludeFault = false;
    }
    else {
        options.excludeFault = true;
    }
    options.sort = 'siteScore^asc';
    options.limit = 1000;
    options.page = 1;

    dispatch(setBobLoading('FetchSiteScore'));
    const manager = ConcurrencyManager(k2aBaseApi, MAX_CONCURRENCY_REQUESTS);

    try {
        const response = await k2aBaseApi.get(
            DashboardDataUrl.expand({ companyId, clientId, siteId, ...options }),
            header
        );

        let meta = _.get(response, 'data.summaryMeta', {});
        let data = _.get(response, 'data.healthcheckSummaries', []);
        let allData = [];
        let total = meta.totalRecordCount;
        let totalPages = total / options.limit;
        let count;
        
        if (Number.isInteger(totalPages)) {
            count = totalPages;
        }
        else {
            count = totalPages + 1;
        }

        let promises = [];
        for (let i = 2; i <= count; i++) {
            options.page = i;
            promises.push (
                k2aBaseApi.get(
                    DashboardDataUrl.expand({ companyId, clientId, siteId, ...options }),
                    header  
                )
            );
        }

        let results = [];
        await Promise.all(promises.map(p => p))
            .then((result) => {
                result.forEach(r => {
                    results.push(r.data.healthcheckSummaries);
                });
            });

        allData = data.concat(...results);

        allData.forEach(entity => {
            const keys = Object.keys(entity).filter(item => item.toLowerCase().includes('score'));

            keys.forEach(key => {
                if (entity[key]) {
                    entity[key] = entity[key] * 100;
                }
            });
            entity.companyId = companyId;
        });

        if (siteId) {
            const selectedSite = allData.find(site => site.siteId === siteId);
            if (selectedSite) {
                allData = [selectedSite];
                meta = { totalHighFaultCount: selectedSite.highFaultCount, totalMediumFaultCount: selectedSite.mediumFaultCount, totalLowFaultCount: selectedSite.lowFaultCount, totalFaultCount: selectedSite.totalFaultCount, totalRecordCount: 1 };
            }
        }

        let siteData = _.orderBy(allData, 'siteScore', 'asc'); 
        dispatch({
            type:    FETCH_SITE_SCORE_DATA,
            payload: {
                data: siteData,
                meta
            }
        });
        dispatch(setBobLoading('FetchSiteScore'));
    } catch (error) {
        dispatch(setBobLoading('FetchSiteScore'));
        dispatch(handleNetworkError(error));
    }
    finally {
        manager.detach();
    }
};

export const setSiteScoreData = (data, meta) => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_SITE_SCORE_DATA,
        payload: {
            data,
            meta
        }
    });
};

export const fetchExecutiveSummarySites = (clientId, options = {}) => async (getState, dispatch) => {
    const header = await awsHeader();

    options.limit || (options.limit = 500);
    options.page || (options.page = 1);

    try {
        const response = await k2aBaseApi.get(
            SitesUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        let sites = _.get(response, 'data.data', []);

        return sites;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchExecutiveSummaryEquipmentSystemTypes = (companyId, attributes, options = {}) => async (getState, dispatch) => {
    const header = await awsHeader();

    try {
        const equipmentTypeAttribute = attributes.find(item => item.name === 'equipmentType');
        const systemTypeAttribute = attributes.find(item => item.name === 'systemType');

        const equipmentTypeResponse = await k2aBaseApi.get(
            AttributeLookupUrl.expand({
                companyId,
                attributeId: equipmentTypeAttribute.id,
                ...options
            }),
            header
        );

        const systemTypeResponse = await k2aBaseApi.get(
            AttributeLookupUrl.expand({
                companyId,
                attributeId: systemTypeAttribute.id,
                ...options
            }),
            header
        );

        let equipmentTypePayload = _.get(equipmentTypeResponse, 'data', []);
        let systemTypePayload = _.get(systemTypeResponse, 'data', []);

        equipmentTypePayload.forEach(item => {
            item.grouping = 'Equipment Type';
            item.name = item.value;
        });

        systemTypePayload.forEach(item => {
            item.grouping = 'System Type';
            item.name = item.value;
        });

        const payload = [...equipmentTypePayload, ...systemTypePayload];

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};
